import useDBO from "../hooks/useDBO";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  Clock,
  UserMinus,
  ClipboardCheck,
} from "lucide-react";

/**
 * Score - Premium dark stats ribbon for Owner role
 * Displays key business metrics in a compact pill layout
 * @returns {JSX.Element}
 * @author Forge
 * @version 2.0.0
 * @since 2025-04-11
 */
export default function Score() {
  const stats = useDBO("/scores", 60);

  if (stats.loading) return <ScoreSkeleton />;
  if (stats.error) return null;

  const today = stats.get[0];
  const yesterday = stats.get[1];

  if (!today || !yesterday) return null;

  return (
    <div className="flex items-center gap-2.5 px-4 py-1.5 bg-slate-900 overflow-x-auto scrollbar-none">
      {statNames.map((s, i) => (
        <StatPill key={s.name + i} stat={s} today={today} yesterday={yesterday} />
      ))}
    </div>
  );
}

/**
 * StatPill - Single compact metric pill
 * @param {object} props
 * @param {object} props.stat - Stat definition { name, label, money, icon }
 * @param {object} props.today - Today's scores object
 * @param {object} props.yesterday - Yesterday's scores object
 * @returns {JSX.Element}
 * @author Forge
 * @version 2.0.0
 */
function StatPill({ stat, today, yesterday }) {
  const value = today[stat.name];
  const prev = yesterday[stat.name];
  const change = prev ? value - prev : 0;

  const Icon = stat.icon;

  return (
    <div className="flex items-center gap-2 bg-slate-800/60 rounded-lg px-3 py-1 text-xs whitespace-nowrap shrink-0">
      <Icon size={13} className="text-slate-500" />
      <span className="text-slate-400">{stat.label}</span>
      <span className="text-white font-semibold">
        {stat.money ? (
          <CompactVND value={value} />
        ) : (
          value?.toLocaleString()
        )}
      </span>
      {change !== 0 && <ChangeIndicator change={change} money={stat.money} />}
    </div>
  );
}

/**
 * ChangeIndicator - Shows green/red trend arrow with delta value
 * @param {object} props
 * @param {number} props.change - The delta value
 * @param {boolean} props.money - Whether to format as currency
 * @returns {JSX.Element}
 * @author Forge
 * @version 2.0.0
 */
function ChangeIndicator({ change, money }) {
  const isUp = change > 0;
  return (
    <span
      className={`flex items-center gap-0.5 font-medium ${
        isUp ? "text-emerald-400" : "text-rose-400"
      }`}
    >
      {isUp ? (
        <TrendingUp size={12} />
      ) : (
        <TrendingDown size={12} />
      )}
      {money ? <CompactVND value={Math.abs(change)} /> : Math.abs(change).toLocaleString()}
    </span>
  );
}

/**
 * CompactVND - Lightweight currency display (no MUI dependency)
 * Formats number as Vietnamese dong without the VND component's input features
 * @param {object} props
 * @param {number} props.value - The number to format
 * @returns {JSX.Element}
 * @author Forge
 * @version 1.0.0
 */
function CompactVND({ value }) {
  if (typeof value !== "number" || isNaN(value)) return <span>—</span>;
  const formatted = new Intl.NumberFormat("vi-VN", {
    style: "currency",
    currency: "VND",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
  return <span>{formatted}</span>;
}

/**
 * ScoreSkeleton - Loading placeholder with pulse animation
 * @returns {JSX.Element}
 * @author Forge
 * @version 1.0.0
 */
function ScoreSkeleton() {
  return (
    <div className="flex items-center gap-2.5 px-4 py-1.5 bg-slate-900 overflow-x-auto scrollbar-none">
      {Array.from({ length: 7 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-2 bg-slate-800/40 rounded-lg px-3 py-1 animate-pulse"
        >
          <div className="w-3 h-3 rounded-full bg-slate-700" />
          <div className="w-12 h-3 rounded bg-slate-700" />
          <div className="w-10 h-3 rounded bg-slate-700" />
        </div>
      ))}
    </div>
  );
}

const statNames = [
  { name: "revenue", label: "Revenue", money: true, icon: DollarSign },
  { name: "payments", label: "Payments", money: true, icon: DollarSign },
  { name: "activeStudents", label: "Active", money: false, icon: Users },
  { name: "pendingStudents", label: "Pending", money: false, icon: Clock },
  { name: "stoppedStudentsThisMonth", label: "Stopped", money: false, icon: UserMinus },
  { name: "costs", label: "Costs", money: true, icon: DollarSign },
  { name: "checkIns", label: "Check Ins", money: false, icon: ClipboardCheck },
];
